import { useEffect, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axios";
import { AmountStep, ConfirmStep } from "./DonationWidget";
import PaymentLoading from "../Campaign/CampaignDonation/PaymentLoading";

const PRESET_AMOUNTS = [100, 200, 500, 1000, 2000, 5000];

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;

    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
};

const DonationProcess = ({ type }) => {
  const { id: adId } = useParams();

  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState(0);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
  });

  // ── Read user from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("ngo-afsa-data");
      if (raw) setUserData(JSON.parse(raw));
    } catch {
      console.error("Failed to parse stored user data");
    }
  }, []);

  // ── Step 1 → 2
  const handleDonate = (selectedAmount) => {
    try {
      const raw = localStorage.getItem("ngo-afsa-data");
      if (raw) setUserData(JSON.parse(raw));
    } catch {
      console.error("Failed to parse stored user data");
    }

    setAmount(selectedAmount);
    setStep(2);
  };

  const handlePay = async () => {
    try {
      setLoading(true);

      const scriptLoaded = await loadRazorpayScript();

      if (!scriptLoaded) {
        alert("Failed to load Razorpay SDK");
        setLoading(false);
        return;
      }

      let finalUser = userData;

      // Create user if guest
      if (!userData) {
        if (!formData.name || !formData.email || !formData.phoneNumber) {
          alert("Please fill all fields");
          setLoading(false);
          return;
        }

        setLoading(true);

        const userRes = await axiosInstance.post("/users/create", {
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
        });

        finalUser = userRes.data?.data;
        setUserData(finalUser);
      } else {
        setLoading(true);
      }

      // Create Razorpay Order
      const orderRes = await axiosInstance.post("/payment/order", {
        amount,
      });

      const order = orderRes.data.order;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,

        currency: order.currency,

        name: "AFSA NGO",

        description: "Donation",

        order_id: order.id,
        handler: async function (response) {
          try {
            const { data } = await axiosInstance.post(
              "/payment/transaction",
              {
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature,

                amount,
                type,
                adId,
                note: "",

                userId: finalUser._id,
              }
            );

            if (!data.success) {
              throw new Error("Transaction save failed");
            }

            navigate(
              `/payment-status?status=SUCCESS&amount=${amount}&order_id=${response.razorpay_order_id}`
            );

          } catch (error) {
            console.error(error);
            alert("Payment successful but failed to save donation");
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: finalUser.name,
          email: finalUser.email,
          contact: finalUser.phoneNumber,
        },

        theme: {
          color: "#02323F",
        },

        modal: {
          ondismiss: function () {
            setLoading(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", function (response) {
        setLoading(false);
        navigate(
          `/payment-status?status=FAILED&order_id=${order.id}`
        );
      });

      razorpay.open();
    } catch (error) {
      console.error(error);

      alert("Payment failed");

      setLoading(false);
    }
  };

  return (
    <div className="relative" data-aos="fade-up">
      {/* Back button */}
      {step > 1 && (
        <button
          onClick={() => setStep(1)}
          className="absolute top-2 left-2 z-50 flex items-center gap-1 text-gray-500 hover:text-gray-900"
          data-aos="fade-right"
        >
          <FaArrowLeft size={18} />
          <span className="text-sm">Back</span>
        </button>
      )}

      {step === 1 && (
        <div data-aos="fade-up">
          <AmountStep
            presetAmounts={PRESET_AMOUNTS}
            amount={amount}
            setAmount={setAmount}
            userData={userData}
            onDonate={handleDonate}
          />
        </div>
      )}

      {step === 2 && (
        <div data-aos="fade-up" data-aos-delay="150">
          <ConfirmStep
            amount={amount}
            formData={formData}
            setFormData={setFormData}
            userData={userData}
            onPay={handlePay}
            loading={loading}
          />
        </div>
      )}

      <PaymentLoading loading={loading} />
    </div>
  );
};

export default DonationProcess;
